import { Component, computed, inject, input, InputSignal, output, OutputEmitterRef, Signal, signal } from '@angular/core';
import { MatExpansionPanel, MatExpansionModule } from "@angular/material/expansion";
import { MatIcon } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DatePipe, NgClass } from '@angular/common';
import {MatTimepickerModule} from '@angular/material/timepicker';
import { AmlScanEntity, LatestAmlReport } from '../../../../../../../../../api/api.model';
import { FormControl, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { take } from 'rxjs';
import { ApiService } from '../../../../../../../../../api/api.service';
import { MatDialogRef } from '@angular/material/dialog';
import { SidenavService } from '../../../../../../../../services/sidenav.service';
import { LeaTimerComponent } from '../lea-timer/lea-timer.component';
import { AuthService } from '../../../../../../../../services/auth.service';
import { NormalizeSpacesDirective } from '../../../../../../../../directives/normalize-spaces.directive';

@Component({
  selector: 'app-incident-progress',
  imports: [
    MatExpansionPanel,
    MatExpansionModule,
    MatIcon,
    MatDatepickerModule,
    NgClass,
    MatTimepickerModule,
    ReactiveFormsModule,
    DatePipe,
    LeaTimerComponent,
    NormalizeSpacesDirective
  ],
  templateUrl: './incident-progress.component.html',
  styleUrl: './incident-progress.component.scss'
})
export class IncidentProgressComponent {
  readonly transaction: InputSignal<AmlScanEntity<LatestAmlReport>> = input.required();
  readonly outputUpdateTransaction: OutputEmitterRef<void> = output();

  private readonly _fb: NonNullableFormBuilder = inject(NonNullableFormBuilder);
  private readonly _apiService: ApiService = inject(ApiService);
  public readonly matDialogRef = inject(MatDialogRef);
  public readonly authService: AuthService = inject(AuthService);

  readonly isLoading = signal<boolean>(false);
  readonly expiresInAwaitingLea = signal<number>(0);
  limitedMinDate: Date = new Date();
  limitedTemporaryMinTime = '0:00';
  limitedPermanentMinTime = '0:00';

  form = this._fb.group({
    typeBlock: this._fb.control<string>('', [Validators.required]),
    amount: this._fb.control<string>('', [Validators.required]),
    temporaryDate: this._fb.control<string>('', [Validators.required]),
    temporaryTime: this._fb.control<string>('', [Validators.required]),
    permanentDate: this._fb.control<string>('', [Validators.required]),
    permanentTime: this._fb.control<string>('', [Validators.required]),
  });

  readonly isComplianceRole: Signal<boolean> = computed(() => {
    const account = this.authService.accountDto();
    const personalCluster = Boolean(account && account.cluster);
    const corporateCluster = Boolean(account && account.companyDto && account.companyDto.cluster);
    const viewer = account && account.companyDto && account.companyRole === 'VIEWER';
    const personalCompliance = (account && account.accountType === 'PERSONAL' && account.role === 'COMPLIANCE') && personalCluster;
    const companyCompliance = (account && (account.accountType === 'CORPORATE') && corporateCluster && !viewer);
    if (personalCompliance || companyCompliance) {
      return true;
    } else {
      return false;
    }
  });

  readonly userRole: Signal<string> = computed(() => {
    const account = this.authService.accountDto();
    if (account) {
      return account.role;
    } else {
      return '';
    }
  });

  comment: FormControl<string> = this._fb.control('', [Validators.required]);

  ngOnInit(): void {
    const transaction = this.transaction();
    this.form.controls['amount'].patchValue(String(transaction.amount));
  }

  isToday(date: Date): boolean {
    const today = new Date();
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  }

  handleClosedDatePickerTemporary(timePicker: any, type: 'temporary' | 'permanent'): void {
    switch (type) {
      case 'temporary':
        if (this.form.controls['temporaryDate'].valid) {
          const pickerDate = new Date(this.form.controls['temporaryDate'].getRawValue());
          if (this.isToday(pickerDate)) {
            this.setMinTimeForDatepicker(type);
          }
          setTimeout(() => {
            (timePicker.open());
          }, 0);
        }
        break;
      case 'permanent':
        if (this.form.controls['permanentDate'].valid) {
          const pickerDate = new Date(this.form.controls['permanentDate'].getRawValue());
          if (this.isToday(pickerDate)) {
            this.setMinTimeForDatepicker(type);
          }
          setTimeout(() => {
            (timePicker.open());
          }, 0);
        }
        break;
    }
  }

  setMinTimeForDatepicker(type: 'temporary' | 'permanent'): void {
    const now = new Date();
    let hours = now.getHours();
    let minutes = now.getMinutes();
    if (minutes > 0 && minutes < 30) {
      minutes = 30;
    } else if (minutes > 30) {
      minutes = 0;
      hours = (hours + 1) % 24;
    }
    const hoursStr = String(hours).padStart(2, '0');
    const minutesStr = String(minutes).padStart(2, '0');
    switch (type) {
      case 'temporary':
        this.limitedTemporaryMinTime = `${hoursStr}:${minutesStr}`;
        
        break;
      case 'permanent':
        this.limitedPermanentMinTime = `${hoursStr}:${minutesStr}`;
        break;
    }
  }

  beforeSubmitTemporaryBlock(): void {
    if (this.form.controls['temporaryDate'].valid && this.form.controls['temporaryTime'].valid) {
      this.form.controls['typeBlock'].patchValue('Temporary');
      const transaction = this.transaction();

      const form = this.form.getRawValue();
      const date: Date = this.transformDateAndTimeInUts(form.temporaryDate, form.temporaryTime);

      const body = {
        blockType: form.typeBlock,
        blockedAmount: form.amount,
        blockExpireAt: date
      }
      this.isLoading.set(true);
      this._apiService.postStopWalletTransactionId(transaction.uuid, body).pipe(take(1)).subscribe({
        next: () => {
          this.outputUpdateTransaction.emit();
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
        }
      });
    }
  }

  beforeSubmitPermanentBlock(): void {
    if (this.form.controls['permanentDate'].valid && this.form.controls['permanentTime'].valid) {
      this.form.controls['typeBlock'].patchValue('Permanent');
      const transaction = this.transaction();

      const form = this.form.getRawValue();
      const date: Date = this.transformDateAndTimeInUts(form.permanentDate, form.permanentTime);

      const body = {
        blockType: form.typeBlock,
        blockedAmount: form.amount,
        blockExpireAt: date
      }
      this.isLoading.set(true);
      this._apiService.postStopWalletTransactionId(transaction.uuid, body).pipe(take(1)).subscribe({
        next: () => {
          this.outputUpdateTransaction.emit();
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
        }
      });
    }
  }

  beforeSubmitLeaRequestSubmission(): void {
    if (this.comment.valid) {
      const comment = this.comment.getRawValue();
      const body = {comment};
      const transaction = this.transaction();
      if (transaction.caseId) {
        this.isLoading.set(true);
        this._apiService.putSubmitFreezeRequest(String(transaction.caseId), transaction.uuid, body).pipe(take(1)).subscribe({
          next: () => {
            this.outputUpdateTransaction.emit();
            this.isLoading.set(false);
          },
          error: () => {
            this.isLoading.set(false);
          }
        })
      }
    }
  }

  private transformDateAndTimeInUts(dateIn: string, timeIn: string): Date {
    const date = new Date(dateIn);
    const time = new Date(timeIn);
    const hour = String(time.getHours()).padStart(2, '0');
    const minute = String(time.getMinutes()).padStart(2, '0');
    const updatedDate = date.setHours(+hour, +minute, 0, 0);
    return new Date(updatedDate);
  }
}
